import React, { useEffect, useState } from 'react';
import { Button, Card, CardContent, Typography } from '@mui/material';
import { toast } from 'react-hot-toast';

import { AdminRefreshCheckrReportService, BackgroundCheck, BackgroundCheckService } from 'us-web-services';
import CardStyles from '../../styles/CardStyles';
import { DateUtil, DisplayService } from '../../util';
import EditBackgroundCheck from './EditBackgroundCheck';

interface Props {
  userId: number;
}

function BackgroundCheckComponent(props: Props) {
  const { userId } = props;

  const { classes } = CardStyles();
  const [backgroundCheck, setBackgroundCheck] = useState<BackgroundCheck>();
  const [loaded, setLoaded] = useState(false);

  const getEventLabel = typeId => {
    const events = {
      1: 'Report Created',
      2: 'Invitation Expired',
      3: 'Invitation Completed',
      4: 'Report Completed',
      5: 'Report Suspended',
      6: 'Report Canceled',
    };

    return events[typeId];
  };

  const getReportStatusLabel = statusId => {
    const status = {
      1: 'Pending',
      2: 'Complete',
      3: 'Suspended',
      4: 'Dispute',
      5: 'Canceled',
    };

    return status[statusId];
  };

  const getInvitationStatusLabel = statusId => {
    const status = {
      1: 'Pending',
      2: 'Complete',
      3: 'Expired',
    };

    return status[statusId];
  };

  const getBackgroundCheck = async () => {
    const config = {
      params: {
        sitterId: userId,
      },
    };
    const backgroundCheckResponse = await BackgroundCheckService.findByFilter(
      config,
    );

    setBackgroundCheck(
      backgroundCheckResponse.data.data.length > 0
        ? backgroundCheckResponse.data.data[0]
        : {},
    );
    setLoaded(true);
  };

  const handleRefreshCheckrReport = async () => {
    try {
      await AdminRefreshCheckrReportService.post({ id: userId });

      getBackgroundCheck();
      toast.success('User refreshed in Checkr Report');
    } catch (error) {
      const displayedError = DisplayService.getErrorResponse(
        error,
        'There was an error refreshing user in Checkr Report',
      );

      toast.error(displayedError.message);
    }
  };

  useEffect(() => {
    getBackgroundCheck();
  }, [userId]);

  return (
    <Card className={classes.sectionCard}>
      <CardContent className={classes.sectionContent}>
        <div style={{ display: 'flex', gap: 32, flexWrap: 'wrap' }}>
          <div style={{ minWidth: 250 }}>
            <Typography className={classes.sectionHeader}>
              Background Check
            </Typography>
            {backgroundCheck?.status && (
              <>
                <div className={classes.infoRow}>
                  <span className={classes.infoLabel}>Status</span>
                  <span className={classes.infoValue}>{backgroundCheck.status.label}</span>
                </div>
                <div className={classes.infoRow}>
                  <span className={classes.infoLabel}>Created</span>
                  <span className={classes.infoValue}>
                    {DateUtil.getReadableDateTime(backgroundCheck.created)}
                  </span>
                </div>
                <div className={classes.infoRow}>
                  <span className={classes.infoLabel}>Completed</span>
                  <span className={classes.infoValue}>
                    {DateUtil.getReadableDateTime(backgroundCheck.completed)}
                  </span>
                </div>
                <div className={classes.infoRow}>
                  <span className={classes.infoLabel}>Modified</span>
                  <span className={classes.infoValue}>
                    {DateUtil.getReadableDateTime(backgroundCheck.modified)}
                  </span>
                </div>
                <div className={classes.infoRow}>
                  <span className={classes.infoLabel}>Expires</span>
                  <span className={classes.infoValue}>
                    {DateUtil.getReadableDateTime(backgroundCheck.expiration)}
                  </span>
                </div>
              </>
            )}
            {loaded && (
              <div style={{ marginTop: 16 }}>
                <EditBackgroundCheck
                  backgroundCheck={backgroundCheck}
                  userId={userId}
                  setBackgroundCheck={setBackgroundCheck}
                />
                <Button
                  id='close'
                  variant='outlined'
                  size='small'
                  onClick={handleRefreshCheckrReport}
                  className={classes.actionButton}
                >
                  Refresh Checkr Report
                </Button>
              </div>
            )}
          </div>
          {backgroundCheck?.checkrRequest?.id && (
            <div style={{ minWidth: 280 }}>
              <Typography className={classes.sectionHeader}>
                Checkr Request Details
              </Typography>
              <div className={classes.infoRow}>
                <span className={classes.infoLabel}>Report Status</span>
                <span className={classes.infoValue}>
                  {getReportStatusLabel(backgroundCheck.checkrRequest.reportStatusId)}
                </span>
              </div>
              <div className={classes.infoRow}>
                <span className={classes.infoLabel}>Include Canceled</span>
                <span className={classes.infoValue}>
                  {backgroundCheck.checkrRequest.includeCanceled ? 'Yes' : 'No'}
                </span>
              </div>
              <div className={classes.infoRow}>
                <span className={classes.infoLabel}>Invitation Status</span>
                <span className={classes.infoValue}>
                  {getInvitationStatusLabel(backgroundCheck.checkrRequest.invitationStatusId)}
                </span>
              </div>
              <div className={classes.infoRow}>
                <span className={classes.infoLabel}>Candidate ID</span>
                <span className={classes.infoValue}>
                  {backgroundCheck.checkrRequest.candidateId}
                </span>
              </div>
              <div className={classes.infoRow}>
                <span className={classes.infoLabel}>Created</span>
                <span className={classes.infoValue}>
                  {DateUtil.getReadableDateTime(backgroundCheck.checkrRequest.created)}
                </span>
              </div>
              <div className={classes.infoRow}>
                <span className={classes.infoLabel}>Last Event</span>
                <span className={classes.infoValue}>
                  {getEventLabel(backgroundCheck.checkrRequest.lastEventTypeId)}
                  {backgroundCheck.checkrRequest.lastEventTypeCreated && (
                    <Typography variant='caption' color='text.secondary' sx={{ ml: 1 }}>
                      {DateUtil.getReadableDateTime(backgroundCheck.checkrRequest.lastEventTypeCreated)}
                    </Typography>
                  )}
                </span>
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default BackgroundCheckComponent;
